import { useState, MouseEvent } from 'react';
import { Popover, Theme } from '@mui/material';
import { IconButton } from '@components/iconButton';
import { NotificationPanel } from '@components/notification/NotificationPanel';
import { Badge } from '@components/badge';
import { Bell } from '@assets/svgs';
import { useNotificationSummary } from '@services/notification';
import { useCurrentUser } from '@services/user';
import { useAlertStore } from '@store/AlertStore';
import { getTodaysHoliday, HolidayMode } from '@theme/palettes/getPalette';
import { useApiEnvStore } from '@utils/api-client';
import { shallow } from 'zustand/shallow';

import SpookySound from '@assets/holiday/halloween/scarry-sound.mp3';

export const NotificationBadge = () => {
  const headerHeight = useAlertStore((state) => state.headerHeight);
  const { currentUser } = useCurrentUser();
  const { data } = useNotificationSummary(currentUser?.id);
  const unseenNotificationsCount = data?.unseenCount || 0;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const openPopover = Boolean(anchorEl);
  const [disableHolidayModeFlag] = useApiEnvStore((state) => [state.disableHolidayModeFlag], shallow);
  const holiday = getTodaysHoliday();

  const handleOpenPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);

    if (holiday === HolidayMode.Halloween && !disableHolidayModeFlag) {
      // play spookySound at 70% volume
      const audio = new Audio(SpookySound);
      audio.volume = 0.7;
      audio.play();
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        id='notification-button'
        aria-controls={openPopover ? 'notification-popover' : undefined}
        aria-expanded={openPopover ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleOpenPopover}
        variant='circularDark'
        sx={{
          color: 'text.main',
        }}
      >
        <Badge badgeContent={unseenNotificationsCount}>
          <Bell width='20px' height='20px' />
        </Badge>
      </IconButton>
      <Popover
        id='notification-popover'
        aria-labelledby='notification-button'
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: headerHeight - 8,
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: headerHeight,
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-elevation8': {
            // primary.dark is a light slate on dark page palettes; use the inset surface so the panel sits darker than its cards
            backgroundColor: (theme: Theme) => (theme.palette.theme === 'lio' ? 'primary.dark' : 'background.main'),
            padding: 0,
            borderRadius: 'shape.borderRadius',
            margin: { xs: 0, sm: '0 0 0 44px' },
            width: { xs: '100vw', sm: '400px' },
            overflow: 'hidden',
            marginTop: -1,
          },
        }}
      >
        <NotificationPanel onClosePopover={handleClosePopover} unseenNotificationsCount={unseenNotificationsCount} />
      </Popover>
    </>
  );
};
